import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { User, LogOut, Edit, Save, X, Phone, Mail, HelpCircle, Crown, CreditCard, Calendar, Check, Trash2, Bell, Sun, Moon, BookOpen } from "lucide-react";
import Faq from "@/pages/faq";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { validateInput } from "@/lib/inputValidation";
import InputRestrictionsDialog from "@/components/input-restrictions-dialog";
import ProfileVersionBanner from "@/components/profile-version-banner";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTheme } from "@/hooks/use-theme";
import { Capacitor } from "@capacitor/core";

interface ProfileProps {
  onLogout: () => void;
  onBack: () => void;
  playerId: string | null;
  onShowTour?: () => void;
  initialSection?: string;
}

export default function Profile({ onLogout, onBack, playerId, onShowTour, initialSection }: ProfileProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { isDark, toggleTheme } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: ""
  });
  const [acceptsMarketingEmails, setAcceptsMarketingEmails] = useState(false);
  const [showRestrictionsDialog, setShowRestrictionsDialog] = useState(false);
  const [restrictionMessage, setRestrictionMessage] = useState("");
  const [showSubscriptionPlans, setShowSubscriptionPlans] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  // Fetch user profile data using apiRequest to ensure authentication headers are sent
  const { data: user, isLoading } = useQuery({
    queryKey: ["/api/auth/user"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/auth/user");
      return response.json();
    },
    retry: false,
  });

  // Fetch player data if playerId exists
  const { data: player } = useQuery({
    queryKey: ["/api/players", playerId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/players/${playerId}`);
      return response.json();
    },
    enabled: !!playerId,
    retry: false,
  });

  // Fetch premium subscription data
  const { data: premiumData } = useQuery({
    queryKey: ["/api/premium/subscription", playerId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/premium/subscription/${playerId}`);
      return response.json();
    },
    enabled: !!playerId,
    retry: false,
  });

  // Fetch premium subscription history
  const { data: subscriptionHistory } = useQuery({
    queryKey: ["/api/premium/history", playerId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/premium/history/${playerId}`);
      return response.json();
    },
    enabled: !!playerId,
    retry: false,
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiRequest("PATCH", "/api/auth/update-profile", data);
    },
    onSuccess: () => {
      toast({
        title: t('profile.toasts.updatedTitle', 'Profil mis à jour'),
        description: t('profile.toasts.updatedDesc', 'Votre profil a été mis à jour avec succès.'),
      });
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    },
    onError: (error: any) => {
      toast({
        title: t('profile.toasts.updateFailedTitle', 'Échec de la mise à jour'),
        description: error.message || t('profile.toasts.updateFailedDesc', 'Échec de la mise à jour du profil'),
        variant: "destructive",
      });
    },
  });

  // Set / change password (optionnel — auth passwordless)
  const setPasswordMutation = useMutation({
    mutationFn: async (data: { password: string; confirmPassword: string }) => {
      return await apiRequest("POST", "/api/auth/set-password", data);
    },
    onSuccess: () => {
      toast({ description: t('profile.passwordSaved', 'Mot de passe enregistré avec succès.') });
      setNewPassword("");
      setConfirmNewPassword("");
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    },
    onError: (error: any) => {
      toast({
        description: error.message || t('profile.passwordError', "Échec de l'enregistrement du mot de passe."),
        variant: "destructive",
      });
    },
  });

  const handleSavePassword = () => {
    if (newPassword.length < 8) {
      toast({ description: t('profile.passwordTooShort', 'Le mot de passe doit contenir au moins 8 caractères.'), variant: "destructive" });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast({ description: t('profile.passwordMismatch', 'Les mots de passe ne correspondent pas.'), variant: "destructive" });
      return;
    }
    setPasswordMutation.mutate({ password: newPassword, confirmPassword: confirmNewPassword });
  };

  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("DELETE", "/api/auth/delete-account");
    },
    onSuccess: () => {
      toast({
        title: t('profile.toasts.deletedTitle', 'Compte supprimé'),
        description: t('profile.toasts.deletedDesc', 'Votre compte a été définitivement supprimé.'),
      });
      // Logout and redirect after successful deletion
      setTimeout(() => {
        onLogout();
      }, 2000);
    },
    onError: (error: any) => {
      toast({
        title: t('profile.toasts.deleteFailedTitle', 'Échec de la suppression'),
        description: error.message || t('profile.toasts.deleteFailedDesc', 'Échec de la suppression du compte'),
        variant: "destructive",
      });
    },
  });

  const handleEditClick = () => {
    if (user) {
      setEditedData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || ""
      });
      setIsEditing(true);
    }
  };

  // Pre-populate form data when user data is loaded
  useEffect(() => {
    if (user && !isEditing) {
      setEditedData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || ""
      });
      setAcceptsMarketingEmails(user.acceptsMarketingEmails ?? true); // Default to true if undefined
    }
  }, [user, isEditing]);

  // Scroll to initial section if provided
  useEffect(() => {
    if (initialSection) {
      const element = document.getElementById(initialSection);
      if (element) {
        // Small delay to ensure rendering is complete
        setTimeout(() => {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });

          // Optional: Add a highlight effect
          element.classList.add('ring-2', 'ring-padel-green', 'ring-offset-2');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-padel-green', 'ring-offset-2');
          }, 2000);
        }, 500);
      }
    }
  }, [initialSection, isLoading]);

  const handleSave = () => {
    // Validate first name
    const firstNameValidation = validateInput(editedData.firstName, "first name");
    if (!firstNameValidation.isValid) {
      setRestrictionMessage(firstNameValidation.error || "Invalid first name");
      setShowRestrictionsDialog(true);
      return;
    }

    // Validate last name
    const lastNameValidation = validateInput(editedData.lastName, "last name");
    if (!lastNameValidation.isValid) {
      setRestrictionMessage(lastNameValidation.error || "Invalid last name");
      setShowRestrictionsDialog(true);
      return;
    }

    updateProfileMutation.mutate(editedData);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: ""
    });
  };

  // Premium subscription management
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", "/api/premium/cancel", { playerId });
    },
    onSuccess: () => {
      toast({
        title: "Subscription Cancelled",
        description: "Your subscription has been cancelled. You'll retain premium access until the end of your billing period.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/premium/subscription", playerId] });
      queryClient.invalidateQueries({ queryKey: ["/api/premium/history", playerId] });
      queryClient.invalidateQueries({ queryKey: ["/api/players", playerId] });
    },
    onError: (error: any) => {
      toast({
        title: "Cancellation Failed",
        description: error.message || "Failed to cancel subscription. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleCancelSubscription = () => {
    cancelSubscriptionMutation.mutate();
  };

  const updatePreferencesMutation = useMutation({
    mutationFn: async (checked: boolean) => {
      // We must send all required fields for update-profile
      return await apiRequest("PATCH", "/api/auth/update-profile", {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        acceptsMarketingEmails: checked
      });
    },
    onSuccess: (data, variables) => {
      setAcceptsMarketingEmails(variables);
      toast({
        title: t('common.success') || "Success",
        description: t('profile.preferencesUpdated') || "Preferences updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    },
    onError: (error: any) => {
      // Revert state on error
      setAcceptsMarketingEmails(!updatePreferencesMutation.variables);
      toast({
        title: t('common.error') || "Error",
        description: error.message || "Failed to update preferences",
        variant: "destructive",
      });
    },
  });

  const handleToggleMarketingEmails = (checked: boolean) => {
    // Optimistic update
    setAcceptsMarketingEmails(checked);
    updatePreferencesMutation.mutate(checked);
  };

  const renderSubscriptionPlans = () => (
    <div className="space-y-3">
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-sm font-medium">{t('profile.choosePlan')}</h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowSubscriptionPlans(false)}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Monthly Plan */}
      <div className="border border-border rounded-lg p-3 space-y-2">
        <div className="flex justify-between items-center">
          <div>
            <h5 className="text-sm font-medium">{t('profile.monthly')}</h5>
            <p className="text-xs text-muted-foreground">€1,99/{t('profile.month')}</p>
          </div>
          <Button
            size="sm"
            disabled={true} // Temporarily disabled for payment integration
            className="bg-blue-500 opacity-50 cursor-not-allowed"
          >
            {t('profile.comingSoon')}
          </Button>
        </div>
      </div>

      {/* Yearly Plan */}
      <div className="border border-border rounded-lg p-3 space-y-2 relative">
        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
          <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
            {t('profile.savePercentage')}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <div>
            <h5 className="text-sm font-medium">{t('profile.yearly')}</h5>
            <p className="text-xs text-muted-foreground">€20/{t('profile.year')}</p>
          </div>
          <Button
            size="sm"
            disabled={true} // Temporarily disabled for payment integration
            className="bg-green-500 opacity-50 cursor-not-allowed"
          >
            {t('profile.comingSoon')}
          </Button>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className={`w-full ${isMobile ? 'sm:max-w-md sm:mx-auto' : ''} bg-card ${isMobile ? 'shadow-lg' : ''} min-h-screen`}>
        <div className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (showFaq) {
    return <Faq onBack={() => setShowFaq(false)} />;
  }

  return (
    <div className={`w-full ${isMobile ? 'sm:max-w-md sm:mx-auto shadow-lg' : 'h-full flex flex-col'} bg-card min-h-screen`}>

      {/* 1. Unified sticky header */}
      <header className="bg-padel-green text-white px-4 py-3 flex items-center justify-between sticky top-0 z-50 shadow-lg safe-area-top">
        <div className="flex items-center gap-3">
          {isMobile && (
            <button
              onClick={onBack}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors dark-btn-active"
              data-testid="button-back"
              title="Back to Dashboard"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <h1 className="text-xl font-bold" data-testid="profile-title">{t('profile.myProfile')}</h1>
        </div>
        <button
          onClick={onLogout}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors dark-btn-active"
          data-testid="button-logout"
          title="Logout"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      {/* Profile Content */}
      <main
        className={`${isMobile ? 'p-4' : 'p-6 flex-1 overflow-y-auto'} space-y-4`}
        data-testid="profile-content"
      >

        {/* 2. Identity section */}
        <div>
          {isEditing ? (
            <Card>
              <CardContent className={`pt-4 space-y-4 ${!isMobile ? 'max-w-2xl' : ''}`}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">{t('profile.firstName')}</Label>
                    <Input
                      id="firstName"
                      value={editedData.firstName || user?.firstName || ""}
                      onChange={(e) => setEditedData(prev => ({ ...prev, firstName: e.target.value }))}
                      data-testid="input-first-name"
                      placeholder={user?.firstName || t('profile.enterFirstName')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">{t('profile.lastName')}</Label>
                    <Input
                      id="lastName"
                      value={editedData.lastName || user?.lastName || ""}
                      onChange={(e) => setEditedData(prev => ({ ...prev, lastName: e.target.value }))}
                      data-testid="input-last-name"
                      placeholder={user?.lastName || t('profile.enterLastName')}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">{t('profile.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={editedData.email || user?.email || ""}
                    onChange={(e) => setEditedData(prev => ({ ...prev, email: e.target.value }))}
                    data-testid="input-email"
                    placeholder={user?.email || t('profile.enterEmail')}
                  />
                </div>

                <div>
                  <Label htmlFor="phoneNumber">{t('profile.phone')}</Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={editedData.phoneNumber || user?.phoneNumber || ""}
                    onChange={(e) => setEditedData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    data-testid="input-phone-number"
                    placeholder={user?.phoneNumber || t('profile.enterPhone')}
                  />
                </div>

                <div className="border-t pt-4 mt-2 space-y-3">
                  <div className="flex items-center gap-2">
                    <Label className="font-semibold">{t('profile.passwordSection', 'Mot de passe')}</Label>
                    <span className="text-xs text-muted-foreground">
                      {user?.hasPassword
                        ? t('profile.passwordChangeHint', '(laisse vide pour ne pas changer)')
                        : t('profile.passwordOptionalHint', '(optionnel — connexion aussi possible par lien email)')}
                    </span>
                  </div>
                  <div>
                    <Label htmlFor="newPassword">{t('profile.newPassword', 'Nouveau mot de passe')}</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={user?.hasPassword ? "••••••••" : t('profile.passwordPlaceholder', '8 caractères minimum')}
                      data-testid="input-new-password"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirmNewPassword">{t('profile.confirmPassword', 'Confirmer le mot de passe')}</Label>
                    <Input
                      id="confirmNewPassword"
                      type="password"
                      autoComplete="new-password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      placeholder={user?.hasPassword ? "••••••••" : t('profile.passwordPlaceholder', '8 caractères minimum')}
                      data-testid="input-confirm-new-password"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleSavePassword}
                    disabled={setPasswordMutation.isPending || !newPassword}
                    data-testid="button-save-password"
                    className="w-full"
                  >
                    {setPasswordMutation.isPending
                      ? t('profile.saving')
                      : (user?.hasPassword ? t('profile.changePassword', 'Changer le mot de passe') : t('profile.setPassword', 'Définir un mot de passe'))}
                  </Button>
                </div>

                <div className="flex space-x-2 pt-2">
                  <Button
                    onClick={handleSave}
                    disabled={updateProfileMutation.isPending}
                    data-testid="button-save-profile"
                    className="flex-1"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {updateProfileMutation.isPending ? t('profile.saving') : t('profile.save')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    data-testid="button-cancel-edit"
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t('common.cancel')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-foreground" data-testid="text-user-name">
                  {user?.firstName} {user?.lastName}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditClick}
                  data-testid="button-edit-profile"
                  className="h-7 w-7 p-0 dark-btn-active"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="w-4 h-4 shrink-0" />
                  <span data-testid="text-user-email">{user?.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="w-4 h-4 shrink-0" />
                  <span data-testid="text-user-phone">{user?.phoneNumber}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 3. Compte & Préférences card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">{t('profile.accountPreferences') || "Compte & Préférences"}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Language row */}
            <div className="flex items-center justify-between px-6 py-3">
              <span className="text-sm font-medium">{t('profile.selectLanguage')}</span>
              <LanguageSwitcher />
            </div>

            <div className="mx-6 border-t border-border" />

            {/* Theme row */}
            <div className="flex items-center justify-between px-6 py-3">
              <span className="text-sm font-medium">{t('profile.appearance') || "Apparence"}</span>
              <button
                onClick={toggleTheme}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium bg-background hover:bg-muted transition-colors dark-btn-active"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {isDark ? (t('profile.lightMode') || "Clair") : (t('profile.darkMode') || "Sombre")}
              </button>
            </div>

            <div className="mx-6 border-t border-border" />

            {/* Notifications marketing emails row */}
            <div id="notifications" className="flex items-center justify-between gap-4 px-6 py-3 scroll-mt-24 transition-all duration-300">
              <div className="flex-1 space-y-0.5">
                <p className="text-sm font-medium leading-none">
                  {t('profile.marketingEmails') || "News & Monthly Recap"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('profile.marketingEmailsDesc') || "Receive monthly statistics reports and news about PadELO."}
                </p>
              </div>
              <Switch
                checked={acceptsMarketingEmails}
                onCheckedChange={handleToggleMarketingEmails}
                disabled={updatePreferencesMutation.isPending || user?.email === 'viewer@padelo.app'}
                title={user?.email === 'viewer@padelo.app' ? "Action non autorisée en mode visiteur" : undefined}
                className="data-[state=checked]:dark:bg-blue-900/50"
              />
            </div>

            <div className="mx-6 border-t border-border" />

            {/* Delete account row */}
            <div className="flex items-center px-6 py-3">
              {user?.email === 'viewer@padelo.app' ? (
                <span className="text-sm text-muted-foreground cursor-not-allowed">Mode Visiteur (Lecture Seule)</span>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      className="text-sm text-red-500 underline disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={deleteAccountMutation.isPending}
                    >
                      {deleteAccountMutation.isPending ? t('profile.deleting') : t('profile.deleteAccount')}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-red-600">{t('profile.deleteAccount')}</AlertDialogTitle>
                      <AlertDialogDescription className="space-y-3">
                        <p>{t('profile.areYouSureDelete')}</p>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-800 font-medium">{t('profile.deleteListTitle')}</p>
                          <ul className="text-sm text-red-700 mt-1 space-y-1">
                            <li>• {t('profile.deleteListItem1')}</li>
                            <li>• {t('profile.deleteListItem2')}</li>
                            <li>• {t('profile.deleteListItem3')}</li>
                            <li>• {t('profile.deleteListItem4')}</li>
                            <li>• {t('profile.deleteListItem5')}</li>
                          </ul>
                        </div>
                        <p className="text-sm font-medium text-red-600">
                          {t('profile.actionUndone')}
                        </p>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => deleteAccountMutation.mutate()}
                        className="bg-red-600 hover:bg-red-700"
                        disabled={deleteAccountMutation.isPending}
                      >
                        {deleteAccountMutation.isPending ? t('profile.deleting') : t('profile.yesDelete')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 4. Aide & Danger section */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <HelpCircle className="w-4 h-4" />
              <span>{t('profile.helpSupport')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className={`space-y-3 ${!isMobile ? 'max-w-2xl' : ''}`}>
            <div className="text-sm text-muted-foreground">
              <p className="mb-2">{t('profile.needHelp')}</p>
              <p className="text-xs text-muted-foreground">
                {t('profile.takeTour')}
              </p>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('mailto:contact@padelo.fr', '_blank')}
              className="w-full dark-btn-active"
            >
              <Mail className="w-4 h-4 mr-2" />
              {t('profile.contactAdmin')}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFaq(true)}
              className="w-full dark-btn-active"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              {t('faq.openButton')}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Remove all Spotlight tour seen states by clearing the combined key
                localStorage.removeItem('padelo_tour_seen');

                // Show confirmation toast
                toast({
                  title: t('common.success', 'Succès'),
                  description: t('profile.toursReactivated', 'Les tutoriels ont été réactivés ! Vous les reverrez en naviguant sur l\'application.'),
                });

                if (onShowTour) {
                  onShowTour();
                }
              }}
              className="w-full dark-btn-active"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              {t('profile.showTourAgain')}
            </Button>

          </CardContent>
        </Card>

        {/* 5. Premium card */}
        {(!user?.email || (user.email.toLowerCase().trim() !== 'viewer@padelo.app' && user.email.toLowerCase().trim() !== 'test.ios@gmail.com')) && (
          <Card id="premium-section" className="transition-all duration-300 scroll-mt-24">
            <CardHeader>
              <CardTitle className="text-lg font-medium flex items-center space-x-2">
                <Crown className="w-5 h-5 text-yellow-500" />
                <span>{t('profile.premiumSubscription')}</span>
                {player?.isPremium && (
                  <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                    {t('profile.active')}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className={`space-y-4 ${!isMobile ? 'max-w-2xl' : ''}`}>
              {player?.isPremium ? (
                // Premium user view
                <div className="space-y-3">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <Crown className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-800">
                        Premium {player.premiumType === 'monthly' ? t('profile.monthly').toUpperCase() :
                          player.premiumType === 'yearly' ? t('profile.yearly').toUpperCase() :
                            player.premiumType === 'lifetime' ? t('profile.lifetime').toUpperCase() :
                              player.premiumType?.toUpperCase() || t('profile.active')}
                      </span>
                    </div>

                    <div className="space-y-1">
                      {player.premiumType && (
                        <p className="text-xs text-yellow-700">
                          <span className="font-medium">{t('profile.plan')}:</span> {player.premiumType === 'monthly' ? `${t('profile.monthly')} (€1,99/${t('profile.month')})` : player.premiumType === 'yearly' ? `${t('profile.yearly')} (€20/${t('profile.year')})` : player.premiumType}
                        </p>
                      )}
                      {player.premiumStartDate && (
                        <p className="text-xs text-yellow-700">
                          <span className="font-medium">{t('profile.started')}:</span> {new Date(player.premiumStartDate).toLocaleDateString()}
                        </p>
                      )}
                      {player.premiumEndDate && (
                        <p className="text-xs text-yellow-700">
                          <span className="font-medium">{t('profile.expires')}:</span> {new Date(player.premiumEndDate).toLocaleDateString()}
                        </p>
                      )}
                      {player.premiumEndDate && new Date(player.premiumEndDate) > new Date() && (
                        <p className="text-xs text-green-700">
                          <span className="font-medium">{t('profile.daysRemaining')}:</span> {Math.ceil((new Date(player.premiumEndDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} {t('profile.days')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Expiration Warning */}
                  {player.premiumEndDate && new Date(player.premiumEndDate) > new Date() && (
                    (() => {
                      const daysRemaining = Math.ceil((new Date(player.premiumEndDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                      if (daysRemaining <= 7) {
                        return (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4 text-orange-600" />
                              <span className="text-sm font-medium text-orange-800">
                                {t('profile.subscriptionExpiresIn')} {daysRemaining} {daysRemaining === 1 ? t('profile.day') : t('profile.days')}
                              </span>
                            </div>
                            <p className="text-xs text-orange-700 mt-1">
                              {t('profile.renewContinue')}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    })()
                  )}

                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-foreground">{t('profile.premiumBenefits')}</h4>
                    <div className="space-y-1 text-xs text-muted-foreground">
                      <div className="flex items-center space-x-2">
                        <Check className="w-3 h-3 text-green-500" />
                        <span>{t('profile.benefitClubManagement')}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Check className="w-3 h-3 text-green-500" />
                        <span>{t('profile.benefitClubRankings')}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Check className="w-3 h-3 text-green-500" />
                        <span>{t('profile.benefitLiveElo')}</span>
                      </div>
                    </div>
                  </div>

                  {showSubscriptionPlans ? (
                    renderSubscriptionPlans()
                  ) : (
                    <>
                      {player.premiumType !== 'lifetime' && (
                        <div className="flex flex-col gap-2">
                          {Capacitor.getPlatform() !== 'ios' && (
                            <Button
                              onClick={() => setShowSubscriptionPlans(true)}
                              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-white"
                              disabled={user?.email === 'viewer@padelo.app'}
                              title={user?.email === 'viewer@padelo.app' ? "Action non autorisée en mode visiteur" : undefined}
                            >
                              <Crown className="w-4 h-4 mr-2" />
                              {user?.email === 'viewer@padelo.app' ? "Mode Visiteur (Lecture Seule)" : t('profile.renewExtend')}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelSubscription}
                            disabled={cancelSubscriptionMutation.isPending || user?.email === 'viewer@padelo.app'}
                            title={user?.email === 'viewer@padelo.app' ? "Action non autorisée en mode visiteur" : undefined}
                            className="w-full text-red-600 border-red-200 hover:bg-red-50"
                          >
                            {user?.email === 'viewer@padelo.app' ? "Mode Visiteur (Lecture Seule)" : (cancelSubscriptionMutation.isPending ? t('profile.cancelling') : t('profile.cancelSubscription'))}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                // Free user view
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-3">
                      {t('profile.unlockPremium')}
                    </p>
                  </div>

                  {/* Premium Benefits */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-foreground">{t('profile.premiumBenefits')}</h4>
                    <div className="space-y-1 text-xs text-muted-foreground">
                      <div className="flex items-center space-x-2">
                        <Check className="w-3 h-3 text-green-500" />
                        <span>{t('profile.benefitClubManagement')}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Check className="w-3 h-3 text-green-500" />
                        <span>{t('profile.benefitClubRankings')}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Check className="w-3 h-3 text-green-500" />
                        <span>{t('profile.benefitLiveElo')}</span>
                      </div>
                    </div>
                  </div>

                  {!showSubscriptionPlans ? (
                    Capacitor.getPlatform() !== 'ios' && (
                      <Button
                        onClick={() => setShowSubscriptionPlans(true)}
                        className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-white"
                        disabled={user?.email === 'viewer@padelo.app'}
                        title={user?.email === 'viewer@padelo.app' ? "Action non autorisée en mode visiteur" : undefined}
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        {user?.email === 'viewer@padelo.app' ? "Mode Visiteur (Lecture Seule)" : t('profile.upgradeToPremium')}
                      </Button>
                    )
                  ) : (
                    renderSubscriptionPlans()
                  )}

                  {/* Subscription History */}
                  {subscriptionHistory && subscriptionHistory.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <h4 className="text-sm font-medium text-foreground mb-2">{t('profile.subscriptionHistory')}</h4>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {subscriptionHistory.slice(0, 3).map((sub: any) => (
                          <div key={sub.id} className="flex justify-between items-center text-xs">
                            <div>
                              <span className="font-medium">{sub.subscriptionType}</span>
                              <span className="text-muted-foreground ml-2">
                                {new Date(sub.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs ${sub.status === 'active' ? 'bg-green-100 text-green-800' :
                              sub.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-muted text-foreground'
                              }`}>
                              {sub.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}


      </main>

      {/* Input Restrictions Dialog */}
      <InputRestrictionsDialog
        open={showRestrictionsDialog}
        onOpenChange={setShowRestrictionsDialog}
        onConfirm={() => setShowRestrictionsDialog(false)}
        message={restrictionMessage}
      />

      {/* Version Banner */}
      <ProfileVersionBanner />
    </div>
  );
}
